// components/LookCreationModal.tsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Typography,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/system";
import { useGlobalStyles } from "../../../../theme/global";
import { StyledInputLabel } from "../../../../theme/StyledInputLabel";
import { AvatarGroup, CustomPhotoAvatar } from "../../../../context/types";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: "10px",
    minWidth: "500px",
  },
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// Define proper types for the form data
interface LookFormData {
  name: string;
  age: CustomPhotoAvatar["age"];
  gender: CustomPhotoAvatar["gender"];
  ethnicity: string;
  orientation: CustomPhotoAvatar["orientation"];
  pose: CustomPhotoAvatar["pose"];
  style: CustomPhotoAvatar["style"];
  appearance: string;
}

interface LookCreationModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    data: any,
    type: "generate" | "upload",
    imageFile?: File
  ) => Promise<void>;
  isLoading: boolean;
  group: AvatarGroup;
}

const LookCreationModal: React.FC<LookCreationModalProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading,
  group,
}) => {
  const globalClasses = useGlobalStyles();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [formData, setFormData] = useState<LookFormData>({
    name: `${group.name} Look`,
    age: "Unspecified",
    gender: "Unspecified",
    ethnicity: "Unspecified",
    orientation: "square",
    pose: "half_body",
    style: "Unspecified",
    appearance: "",
  });

  // Prefill form with base avatar data when modal opens or group changes
  useEffect(() => {
    if (open && group?.base_avatar) {
      const baseAvatar = group.base_avatar;
      setFormData((prev) => ({
        ...prev,
        name: `${group.name} Look`,
        age: baseAvatar.age,
        gender: baseAvatar.gender,
        ethnicity: baseAvatar.ethnicity,
        orientation: baseAvatar.orientation,
        pose: baseAvatar.pose,
        style: baseAvatar.style,
        // Keep appearance empty - this is the only field we don't prefill
        appearance: "",
      }));
    }
  }, [open, group]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setFormData({
        name: `${group.name} Look`,
        age: "Unspecified",
        gender: "Unspecified",
        ethnicity: "Unspecified",
        orientation: "square",
        pose: "half_body",
        style: "Unspecified",
        appearance: "",
      });
      setSelectedFile(null);
    }
  }, [open, group.name]);

  const handleChange = (field: keyof LookFormData) => (event: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert("File size must be less than 10MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmit = async () => {
    if (activeTab === 0) {
      // Generate look - ADD GROUP ID TO THE DATA
      await onSubmit(
        {
          ...formData,
          group_id: group.group_id, // Add group ID for look generation
        },
        "generate"
      );
    } else {
      // Upload look
      if (!selectedFile) {
        alert("Please select an image file");
        return;
      }
      await onSubmit(formData, "upload", selectedFile);
    }

    // Don't reset form here anymore - moved to useEffect
  };

  const canSubmit =
    formData.name.trim() !== "" &&
    !isLoading &&
    (activeTab === 0 || (activeTab === 1 && selectedFile));

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Add Look to {group.name}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Create a new look variation for your avatar group. Base avatar
          attributes are pre-filled.
        </Typography>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
          >
            <Tab
              label="Generate Look"
              sx={{
                color: "text.primary",
                "&.Mui-selected": { color: "primary.main" },
              }}
            />
          </Tabs>
        </Box>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              className={globalClasses.textFieldLabel}
              fullWidth
              label="Look Name *"
              value={formData.name}
              onChange={handleChange("name")}
              placeholder="Enter a name for this look"
            />
          </Grid>

          {/* Upload Tab Content */}
          {activeTab === 1 && (
            <>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <StyledInputLabel>Gender</StyledInputLabel>
                  <Select
                    value={formData.gender}
                    onChange={handleChange("gender")}
                    label="Gender"
                  >
                    <MenuItem value="Woman">Woman</MenuItem>
                    <MenuItem value="Man">Man</MenuItem>
                    <MenuItem value="Unspecified">Unspecified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {selectedFile ? selectedFile.name : "Upload Look Image"}
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </Button>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Supported formats: JPG, PNG, WebP. Max size: 10MB
                </Typography>
              </Grid>
            </>
          )}

          {/* Generate Tab Content */}
          {activeTab === 0 && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <StyledInputLabel>Age</StyledInputLabel>
                  <Select
                    value={formData.age}
                    onChange={handleChange("age")}
                    label="Age"
                  >
                    <MenuItem value="Young Adult">Young Adult</MenuItem>
                    <MenuItem value="Early Middle Age">
                      Early Middle Age
                    </MenuItem>
                    <MenuItem value="Late Middle Age">Late Middle Age</MenuItem>
                    <MenuItem value="Senior">Senior</MenuItem>
                    <MenuItem value="Unspecified">Unspecified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <StyledInputLabel>Gender</StyledInputLabel>
                  <Select
                    value={formData.gender}
                    onChange={handleChange("gender")}
                    label="Gender"
                  >
                    <MenuItem value="Woman">Woman</MenuItem>
                    <MenuItem value="Man">Man</MenuItem>
                    <MenuItem value="Unspecified">Unspecified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <StyledInputLabel>Ethnicity</StyledInputLabel>
                  <Select
                    value={formData.ethnicity}
                    onChange={handleChange("ethnicity")}
                    label="Ethnicity"
                  >
                    <MenuItem value="White">White</MenuItem>
                    <MenuItem value="Black">Black</MenuItem>
                    <MenuItem value="Asian American">Asian American</MenuItem>
                    <MenuItem value="East Asian">East Asian</MenuItem>
                    <MenuItem value="South East Asian">South East Asian</MenuItem>
                    <MenuItem value="South Asian">South Asian</MenuItem>
                    <MenuItem value="Middle Eastern">Middle Eastern</MenuItem>
                    <MenuItem value="Pacific">Pacific</MenuItem>
                    <MenuItem value="Hispanic">Hispanic</MenuItem>
                    <MenuItem value="Unspecified">Unspecified</MenuItem>

                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <StyledInputLabel>Orientation</StyledInputLabel>
                  <Select
                    value={formData.orientation}
                    onChange={handleChange("orientation")}
                    label="Orientation"
                  >
                    <MenuItem value="square">Square</MenuItem>
                    <MenuItem value="horizontal">Horizontal</MenuItem>
                    <MenuItem value="vertical">Vertical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <StyledInputLabel>Pose</StyledInputLabel>
                  <Select
                    value={formData.pose}
                    onChange={handleChange("pose")}
                    label="Pose"
                  >
                    <MenuItem value="half_body">Half Body</MenuItem>
                    <MenuItem value="close_up">Close Up</MenuItem>
                    <MenuItem value="full_body">Full Body</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <StyledInputLabel>Style</StyledInputLabel>
                  <Select
                    value={formData.style}
                    onChange={handleChange("style")}
                    label="Style"
                  >
                    <MenuItem value="Realistic">Realistic</MenuItem>
                    <MenuItem value="Pixar">Pixar</MenuItem>
                    <MenuItem value="Cinematic">Cinematic</MenuItem>
                    <MenuItem value="Vintage">Vintage</MenuItem>
                    <MenuItem value="Noir">Noir</MenuItem>
                    <MenuItem value="Cyberpunk">Cyberpunk</MenuItem>
                    <MenuItem value="Unspecified">Unspecified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  className={globalClasses.textFieldLabel}
                  fullWidth
                  multiline
                  rows={3}
                  label="Appearance Description"
                  value={formData.appearance}
                  onChange={handleChange("appearance")}
                  placeholder="Describe how this look should differ from the base avatar (clothing, hair, accessories, etc.)"
                  helperText="Describe the specific changes for this look variation"
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ padding: "20px" }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading
            ? activeTab === 0
              ? "Generating Look..."
              : "Uploading Look..."
            : activeTab === 0
            ? "Generate Look"
            : "Upload Look"}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default LookCreationModal;
